import { FormEvent, useState } from "react";
import { Modal } from "../Modal";
import { Pairs, RecordSheet, SectionBar } from "../RecordSections";
import { formatSchoolDate } from "../students/studentDisplay";
import { apiFieldErrors } from "../../utils/apiError";
import type { StaffProfile, Training, TrainingAttendance } from "../../services/staffService";
import {
  TRAINING_PHASE_DOTS,
  TRAINING_PHASE_TABS,
  outstandingStaff,
  sessionStats,
  trainingPhase,
  trainingTypeLabel,
} from "../../pages/trainingFilters";

export const PASS_MARK = 50;

export type TrainingResultValues = {
  training: number;
  staff: number;
  attended: boolean;
  score?: string;
  passed: boolean;
  completion_date?: string;
  feedback?: string;
};

export function TrainingPhaseLabel({ training }: { training: Training }) {
  const phase = trainingPhase(training);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
      <span className={`h-1.5 w-1.5 rounded-full ${TRAINING_PHASE_DOTS[phase]}`} />
      {TRAINING_PHASE_TABS.find(([value]) => value === phase)?.[1]}
    </span>
  );
}

export default function TrainingSessionModal({
  training,
  records,
  profiles,
  canManage,
  isSaving,
  error,
  onRecord,
  onClose,
}: {
  training: Training;
  records: TrainingAttendance[];
  profiles: StaffProfile[];
  canManage: boolean;
  isSaving: boolean;
  error: unknown;
  onRecord: (values: TrainingResultValues, done: () => void) => void;
  onClose: () => void;
}) {
  const [staff, setStaff] = useState("");
  const [attended, setAttended] = useState(true);
  const [score, setScore] = useState("");
  const [feedback, setFeedback] = useState("");
  const { fields: errors, message } = apiFieldErrors(
    error,
    ["staff", "score", "completion_date", "feedback"],
    "The result could not be recorded. Please try again.",
  );
  const stats = sessionStats(training.id, records);
  const recorded = new Set(stats.records.map((record) => record.staff));
  const recordable = profiles.filter(
    (profile) => ["active", "probation", "suspended"].includes(profile.employment_status) && !recorded.has(profile.id),
  );
  const outstanding = training.is_mandatory ? outstandingStaff(training.id, records, profiles) : [];
  const working = profiles.filter((profile) => ["active", "probation"].includes(profile.employment_status)).length;
  const started = trainingPhase(training) !== "upcoming";
  const passed = attended && (score === "" ? true : Number(score) >= PASS_MARK);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onRecord(
      {
        training: training.id,
        staff: Number(staff),
        attended,
        score: attended && score !== "" ? score : undefined,
        passed,
        completion_date: attended ? training.end_date : undefined,
        feedback: feedback.trim() || undefined,
      },
      () => {
        setStaff("");
        setScore("");
        setFeedback("");
        setAttended(true);
      },
    );
  };

  return (
    <Modal
      isOpen
      size="3xl"
      title="Training session"
      onClose={() => {
        if (!isSaving) onClose();
      }}
      footer={
        <button type="button" className="btn btn-outline" disabled={isSaving} onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="space-y-4">
        <RecordSheet>
          <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold leading-6 text-gray-900">{training.name}</h2>
              <p className="text-xs text-gray-500">
                {trainingTypeLabel(training.training_type)} · organised by {training.organizer_name}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
              <span className="rounded border border-gray-200 px-2 py-0.5">
                <TrainingPhaseLabel training={training} />
              </span>
              {training.is_mandatory && (
                <span className="rounded bg-red-50 px-2 py-0.5 text-red-700">Mandatory</span>
              )}
            </div>
          </header>

          <SectionBar letter="A" title="Session" />
          <Pairs
            rows={[
              [
                "Dates",
                training.start_date === training.end_date
                  ? formatSchoolDate(training.start_date)
                  : `${formatSchoolDate(training.start_date)} to ${formatSchoolDate(training.end_date)}`,
              ],
              ["Hours", `${training.duration_hours}`],
              ["Venue", training.venue],
              ["Trainer", training.trainer],
              ["Description", training.description],
              ["Objectives", training.objectives],
            ]}
          />

          <SectionBar
            letter="B"
            title={`Attendance & results (${stats.attended} attended · ${stats.passed} passed)`}
          />
          <div className="overflow-x-auto px-4 py-3">
            {stats.records.length ? (
              <table className="w-full text-left">
                <thead className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-8 py-1 pr-3 font-semibold">No.</th>
                    <th className="py-1 pr-3 font-semibold">Staff member</th>
                    <th className="py-1 pr-3 font-semibold">Attended</th>
                    <th className="py-1 pr-3 text-right font-semibold">Score</th>
                    <th className="py-1 pr-3 font-semibold">Result</th>
                    <th className="py-1 font-semibold">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.records
                    .slice()
                    .sort((a, b) => a.staff_name.localeCompare(b.staff_name))
                    .map((record, index) => (
                      <tr key={record.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-1.5 pr-3 text-gray-500">{index + 1}.</td>
                        <td className="py-1.5 pr-3 font-medium text-gray-900">{record.staff_name}</td>
                        <td className="py-1.5 pr-3">{record.attended ? "Yes" : <span className="text-red-700">No</span>}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {record.score != null && record.score !== "" ? `${Number(record.score)}%` : "—"}
                        </td>
                        <td className="py-1.5 pr-3">
                          {record.passed ? (
                            <span className="font-medium text-green-700">Passed</span>
                          ) : (
                            <span className="text-gray-500">{record.attended ? "Not passed" : "—"}</span>
                          )}
                        </td>
                        <td className="max-w-[14rem] truncate py-1.5 text-gray-600" title={record.feedback || undefined}>
                          {record.feedback || "—"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500">No attendance recorded yet.</p>
            )}
          </div>

          {training.is_mandatory && (
            <>
              <SectionBar letter="C" title={`Still to complete (${outstanding.length} of ${working})`} />
              <div className="px-4 py-3">
                {outstanding.length ? (
                  <p className="leading-6 text-gray-700">
                    {outstanding.map((profile) => profile.user_name).join(", ")}
                  </p>
                ) : (
                  <p className="font-medium text-green-700">Everyone on the working staff has completed it.</p>
                )}
              </div>
            </>
          )}
        </RecordSheet>

        {canManage && (
          <form onSubmit={submit} className="space-y-3 rounded-lg border border-gray-200 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Record a result</h3>
              <p className="text-xs text-gray-500">
                Pass mark {PASS_MARK}%. {started ? "" : "The session has not started yet."}
              </p>
            </div>
            {message && (
              <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {message}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_8rem]">
              <label className="block text-sm font-medium text-gray-700">
                Staff member *
                <select required value={staff} onChange={(event) => setStaff(event.target.value)} className="input mt-1">
                  <option value="">
                    {recordable.length ? "Choose a staff member" : "Everyone has a result"}
                  </option>
                  {recordable.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.user_name} · {profile.position_title}
                    </option>
                  ))}
                </select>
                {errors.staff && <span role="alert" className="mt-1 block text-xs font-normal text-red-700">{errors.staff}</span>}
              </label>
              <label className="flex items-center gap-2 self-end pb-2.5 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={attended}
                  onChange={(event) => setAttended(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Attended
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Score %
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={score}
                  disabled={!attended}
                  onChange={(event) => setScore(event.target.value)}
                  className="input mt-1 tabular-nums"
                  placeholder="Optional"
                />
              </label>
            </div>
            <label className="block text-sm font-medium text-gray-700">
              Feedback
              <input
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                className="input mt-1"
                placeholder="Optional, e.g. Strong on practical skills"
              />
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-gray-500">
                {attended
                  ? score === ""
                    ? "No score: counted as passed on attendance."
                    : passed
                      ? "Will be recorded as passed."
                      : `Below ${PASS_MARK}%: recorded as not passed.`
                  : "Recorded as absent."}
              </p>
              <button type="submit" disabled={isSaving || !staff} className="btn btn-primary">
                {isSaving ? "Saving…" : "Record result"}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
